import React, { useState, useRef } from 'react';

/**
 * SUIVI DE STAGE — l'éditeur des documents de retour (lot 3.8b)
 * ==============================================================
 * Maquette : #docs-modele-editor — nom du modèle, « Documents à collecter », la
 * convention signée VERROUILLÉE en tête (cadenas, libellé grisé, « Obligatoire »
 * figé, PDF), puis un bloc par document (poignée, libellé, « Obligatoire » en
 * bascule, corbeille, formats), « Ajouter un document », Enregistrer / Annuler.
 *
 * ── CE QUI S'AJOUTE À LA MAQUETTE ──────────────────────────────────────────
 *   · les FORMATS se choisissent (PDF, ou PDF + image) — E114 ;
 *   · ⚠️ un document DÉJÀ DÉPOSÉ ne se supprime pas, il se DÉSACTIVE (E113) : la
 *     corbeille devient « Désactiver ». Il quitte le portail des PROCHAINES
 *     campagnes et reste lisible dans les dossiers qui l'ont reçu ;
 *   · le bandeau d'E101 quand une campagne a figé sa liste (E115).
 */

let compteur = 0;
const cle = () => `d${++compteur}`;

function StageEditeurDocuments({ modele, modifiable, enCours, erreur, onEnregistrer, onAnnuler, onApercu }) {
    const [nom, setNom] = useState(modele.nom || '');
    const [items, setItems] = useState(() => (modele.items?.length ? modele.items
        : [{ id: null, libelle: 'Convention signée', obligatoire: true, formats_acceptes: 'pdf', verrouille: true, actif: true }])
        .map((x) => ({ ...x, _cle: cle() })));
    const glisse = useRef(null);
    const lectureSeule = !modifiable;
    const figees = (modele.utilise_dans || []).filter((u) => u.figee);

    const poser = (k, champ, v) => setItems((xs) => xs.map((x) => (x._cle === k ? { ...x, [champ]: v } : x)));
    const retirer = (k) => setItems((xs) => xs.filter((x) => x._cle !== k));
    const ajouter = () => setItems((xs) => [...xs, { id: null, _cle: cle(), libelle: '', obligatoire: true, formats_acceptes: 'pdf,image', actif: true }]);
    /** Réordonner — jamais au-dessus de la convention, qui reste en tête. */
    const deplacer = (depart, cible) => setItems((xs) => {
        if (depart === cible || xs[depart]?.verrouille || xs[cible]?.verrouille) return xs;
        const copie = [...xs]; const [x] = copie.splice(depart, 1); copie.splice(cible, 0, x); return copie;
    });

    const charge = () => ({ nom, items: items.map((x) => ({ id: x.id, libelle: x.libelle, obligatoire: x.obligatoire !== false,
        formats_acceptes: x.formats_acceptes, verrouille: x.verrouille === true, actif: x.actif !== false })) });

    return (
        <div className="modele-editor">
            <div className="modele-editor-header">
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--stg-vert-d)' }}>
                    <i className="fa-solid fa-pen" /> {modele.id ? 'Éditeur' : 'Nouveau modèle'} — Documents retour
                </div>
                <button type="button" className="btn btn-outline btn-sm" onClick={onAnnuler}><i className="fa-solid fa-xmark" /> Fermer</button>
            </div>

            {figees.length > 0 && (
                <div className="stg-encart stg-encart-alerte">
                    <i className="fa-solid fa-lock" />
                    <span>
                        Ce modèle est utilisé par <b>{figees.length} campagne{figees.length > 1 ? 's' : ''} déjà lancée{figees.length > 1 ? 's' : ''}</b>
                        {' '}({figees.map((u) => u.libelle).join(', ')}) : leurs étudiants déposent selon la liste de leur premier envoi.
                        Votre modification vaudra pour les copies de ce processus (Dupliquer, Récupérer N-1).
                    </span>
                </div>
            )}

            <div className="form-group" style={{ marginBottom: 14 }}>
                <label htmlFor="docs-nom">Nom du modèle <span style={{ color: '#dc2626' }}>*</span></label>
                <input id="docs-nom" className="input-sm" style={{ maxWidth: 300 }} value={nom} disabled={lectureSeule} onChange={(e) => setNom(e.target.value)} />
            </div>
            <div className="modele-sous-titre">Documents à collecter</div>
            <div style={{ fontSize: 11, color: 'var(--stg-muted)', marginBottom: 12 }}>
                <i className="fa-solid fa-circle-info" /> La convention signée est toujours présente et non supprimable.
            </div>

            {items.map((x, i) => (
                <div key={x._cle} className="critere-block" style={x.actif === false ? { opacity: 0.55 } : undefined}
                     onDragOver={(e) => e.preventDefault()}
                     onDrop={(e) => { e.preventDefault(); if (glisse.current !== null) deplacer(glisse.current, i); glisse.current = null; }}>
                    <div className="critere-block-header" style={{ marginBottom: 4 }}>
                        {x.verrouille ? (
                            <i className="fa-solid fa-lock" style={{ fontSize: 11, color: 'var(--stg-muted)', padding: 2 }} title="Toujours présent" />
                        ) : (
                            <button type="button" className="critere-drag" draggable={!lectureSeule} disabled={lectureSeule}
                                    title="Glisser pour réordonner (ou flèches haut / bas)" aria-label={`Déplacer le document ${i + 1}`}
                                    onDragStart={(e) => { glisse.current = i; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }}
                                    onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); deplacer(i, i - 1); } if (e.key === 'ArrowDown') { e.preventDefault(); deplacer(i, i + 1); } }}>
                                <i className="fa-solid fa-grip-vertical" />
                            </button>
                        )}
                        <input className="input-sm critere-label" value={x.libelle} placeholder="Libellé du document…"
                               readOnly={x.verrouille} disabled={lectureSeule || x.actif === false}
                               style={x.verrouille ? { background: '#f8fafc', color: 'var(--stg-muted)', cursor: 'not-allowed' } : undefined}
                               aria-label={`Libellé du document ${i + 1}`} onChange={(e) => poser(x._cle, 'libelle', e.target.value)} />
                        {x.actif === false && <span className="modeles-usage-chip">désactivé</span>}
                        <span style={{ fontSize: 11, color: 'var(--stg-muted)', whiteSpace: 'nowrap', marginLeft: 'auto' }}>{x.obligatoire !== false ? 'Obligatoire' : 'Facultatif'}</span>
                        <button type="button" className={'toggle' + (x.obligatoire !== false ? ' on' : '') + (x.verrouille ? ' cfg-locked' : '')}
                                disabled={lectureSeule || x.verrouille || x.actif === false} aria-pressed={x.obligatoire !== false}
                                title={x.verrouille ? 'Toujours obligatoire' : 'Obligatoire / Facultatif'} aria-label={`Obligatoire — document ${i + 1}`}
                                onClick={() => poser(x._cle, 'obligatoire', x.obligatoire === false)} />
                        {!x.verrouille && (x.utilise ? (
                            <button type="button" className="action-btn" disabled={lectureSeule}
                                    title={x.actif === false ? 'Réactiver ce document' : 'Déjà déposé par des étudiants : il se désactive, il ne se supprime pas'}
                                    onClick={() => poser(x._cle, 'actif', x.actif === false)}>
                                <i className={'fa-solid ' + (x.actif === false ? 'fa-eye' : 'fa-eye-slash')} />
                            </button>
                        ) : (
                            <button type="button" className="action-btn" title="Supprimer ce document" disabled={lectureSeule}
                                    style={{ color: '#dc2626', borderColor: '#fecaca' }} onClick={() => retirer(x._cle)}>
                                <i className="fa-solid fa-trash" />
                            </button>
                        ))}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--stg-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        Formats :
                        {x.verrouille ? <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: 3, fontSize: 10 }}>PDF</code> : (
                            <select className="input-sm" style={{ width: 'auto', fontSize: 11 }} value={x.formats_acceptes} disabled={lectureSeule || x.actif === false}
                                    aria-label={`Formats — document ${i + 1}`} onChange={(e) => poser(x._cle, 'formats_acceptes', e.target.value)}>
                                <option value="pdf">PDF</option>
                                <option value="pdf,image">PDF + image</option>
                            </select>
                        )}
                        {x.actif === false && <span>— n'apparaîtra plus sur le portail des prochaines campagnes</span>}
                    </div>
                </div>
            ))}

            <div style={{ marginTop: 8 }}>
                <button type="button" className="btn btn-outline btn-sm" disabled={lectureSeule} onClick={ajouter}><i className="fa-solid fa-plus" /> Ajouter un document</button>
            </div>

            {erreur && <div className="stg-encart stg-encart-erreur" style={{ marginTop: 12 }}><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

            <div className="modele-actions">
                <button type="button" className="btn btn-green btn-sm" disabled={lectureSeule || enCours} onClick={() => onEnregistrer(charge())}>
                    {enCours ? <><i className="fa-solid fa-circle-notch stg-tourne" /> Enregistrement…</> : <><i className="fa-solid fa-check" /> Enregistrer</>}
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={onAnnuler}>Annuler</button>
                <button type="button" className="lien-apercu" onClick={() => onApercu(charge())}><i className="fa-solid fa-eye" /> Visualiser le portail</button>
            </div>
            {lectureSeule && <div style={{ fontSize: 11, color: 'var(--stg-muted)', marginTop: 6 }}><i className="fa-solid fa-circle-info" /> Lecture seule : votre profil ne permet pas de modifier ce module.</div>}
        </div>
    );
}

export default StageEditeurDocuments;
